import React, { useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { ErrorAlert } from "@/components/ErrorAlert";
import { PortalAd } from "./types";
import { useAccountContext, useRemoveDiscountCode } from "./AccountContext";
import { BoxList } from "./BoxList";
import { AddProductDrawer } from "./AddProductDrawer";
import { DrawerPageProps } from "./PortalDrawer/types";
import { InactiveSubscriptionsList } from "./InactiveSubscriptionsList";
import { ErrorSubscriptionsList } from "./ErrorSubscriptionsList";
import { RootBoxItemDrawer } from "./RootBoxItemDrawer";
import { EditSubscriptionPage } from "./PortalDrawer/pages/EditItemPage";
import { NotificationManagement } from "../notification/NotificationManagement";
import { NotificationProps } from "../notification/Notification";
import { useIsMutating } from "@tanstack/react-query";
import { EditDiscountDrawer } from "./EditDiscountDrawer";
import { DiscountBadge } from "./DiscountBadge";
import { Button } from "@/components/Button";
import { BoxDrawerPageProps, RootBoxDrawer } from "./RootBoxDrawer";
import { OrderNowPage } from "./PortalDrawer/pages/OrderNowPage";
import { DelayShipmentPage } from "./PortalDrawer/pages/DelayItemPage";
import { SkipShipmentPage } from "./PortalDrawer/pages/SkipShipmentPage";
import { ConsolidateShipmentPage } from "./PortalDrawer/pages/ConsolidateItemsPage";
import { useMediaQuery } from "@mui/material";
import { theme } from "@/theme";

type RootDrawerPageId =
  | "edit"
  | "delay"
  | "consolidate-shipment"
  | "skip"
  | "swap-item";
import { Ad } from "./Ad";
import { getAd, getUltrawashAd } from "./AdManagement";
import { track } from "@/helpers/googleAnalytics";

const RootDrawerPage = ({
  page,
  ...rest
}: { page: RootDrawerPageId } & DrawerPageProps) => {
  switch (page) {
    case "edit":
      return <EditSubscriptionPage {...rest} />;
    case "swap-item":
      return (
        <EditSubscriptionPage
          initialPage={{
            kind: "swap-item",
            isSubscription: false,
            cadence: null,
            initialFilter: "Ultrawash Plus",
          }}
          {...rest}
        />
      );
    case "delay":
      return (
        <EditSubscriptionPage
          initialPage={{ kind: "delay-shipment" }}
          {...rest}
        />
      );
    case "skip":
      return (
        <EditSubscriptionPage
          initialPage={{ kind: "skip-shipment" }}
          {...rest}
        />
      );
    case "consolidate-shipment":
      return (
        <EditSubscriptionPage
          initialPage={{ kind: "consolidate-shipment" }}
          {...rest}
        />
      );
  }
};

type RootBoxDrawerPageId =
  | "order-now"
  | "delay-order"
  | "consolidate-shipment"
  | "skip-shipment";

const RootBoxDrawerPage = ({
  page,
  ...rest
}: {
  page: RootBoxDrawerPageId;
} & BoxDrawerPageProps) => {
  switch (page) {
    case "order-now":
      return <OrderNowPage {...rest} />;
    case "delay-order":
      return <DelayShipmentPage shipment={rest.box} {...rest} />;
    case "skip-shipment":
      return <SkipShipmentPage {...rest} />;
    case "consolidate-shipment":
      return <ConsolidateShipmentPage {...rest} />;
  }
};

export const PortalManagement = () => {
  const requestInProgress = !!useIsMutating();
  const { boxSchedule, boxItems, allItems } = useAccountContext();
  const [selectedOrderId, setSelectedOrderId] = useState<number>(0);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string>("0");
  const [isProductDrawerOpen, setProductDrawerOpen] = useState(false);
  const [drawerPage, setDrawerPage] = useState<RootDrawerPageId | undefined>(
    undefined
  );
  const errorSubs = useMemo(() => {
    const subs = allItems.filter(
      (item) =>
        item.rechargeItem.nextChargeDate === undefined &&
        item.rechargeItem.status === "ACTIVE"
    );
    return subs;
  }, [allItems]);

  const isMobile = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.lg)
  );

  const [rootBoxDrawerTitle, setRootBoxDrawerTitle] =
    useState<string>("Edit Box");
  const [ShipmentDrawerPage, setShipmentDrawerPage] = useState<
    RootBoxDrawerPageId | undefined
  >(undefined);
  const [isDiscountDrawerOpen, setDiscountDrawerOpen] = useState(false);
  const [addProductDrawerFilter, setAddProductDrawerFilter] = useState<
    string | undefined
  >(undefined);

  const [currNotification, setCurrNotification] =
    useState<null | NotificationProps>(null);

  const notify = ({ title, message, severity }: NotificationProps) => {
    setCurrNotification({ title, message, severity });
  };

  const handleReactivate = (title: string) => {
    notify({
      title: "Subscription Reactivated",
      message: `Your subscription to ${title} has been reactivated.`,
    });
  };

  const schedule = boxSchedule.getScheduleAscending();

  const firstShipment =
    schedule.length > 0 ? boxSchedule.getScheduleAscending()[0] : undefined;

  const remainingShipments = schedule.slice(1);
  const selectedShipment = schedule.find((x) => x.id === selectedShipmentId);
  const firstAvailableAddressId = firstShipment?.address.id;
  const activeDiscount = firstShipment?.discount?.name;

  const removeDiscount = useRemoveDiscountCode();
  const onRemoveDiscount = (id: number) => {
    removeDiscount.mutate(id, {
      onSuccess: () => {
        notify({
          title: "Discount Removed",
          message: "Your discount code was removed.",
        });
      },
      onError: () => {
        notify({
          title: "Error",
          message: "There was an error removing your discount code.",
          severity: "error",
        });
      },
    });
  };

  const selectedItem = boxItems.find(
    (x) => x.rechargeItem.id === selectedOrderId
  );

  const subProducts = boxItems.map((s) => s.product);

  const containsUltraWash = subProducts.some((p) =>
    p.tags.includes("Dishwasher Detergent Pods")
  );

  const ad = getAd(subProducts);

  const ultraWashAd = getUltrawashAd(subProducts);

  const handleAdClick = (clickedAd: PortalAd) => {
    setAddProductDrawerFilter(clickedAd.initialFilter);
    setSelectedOrderId(
      boxSchedule.getScheduleAscending()[0].items[0].rechargeItem.id
    );
    setProductDrawerOpen(true);
    track({ name: "Event", eventName: `portal_ad_${clickedAd.name}` });
  };
  const handleUltrawashUpgrade = (clickedAd: PortalAd) => {
    const dishProduct = boxItems.find((boxItem) =>
      boxItem.product.tags.includes("Dishwasher Detergent Pods")
    );
    if (!dishProduct) return;
    setDrawerPage("swap-item");
    setSelectedOrderId(dishProduct.rechargeItem.id);
    track({ name: "Event", eventName: `portal_ad_${clickedAd.name}` });
  };

  return (
    <div className="space-y-8 lg:space-y-12 xl:-mx-4">
      <Sentry.ErrorBoundary fallback={ErrorAlert}>
        {errorSubs.length > 0 && (
          <ErrorSubscriptionsList
            requestInProgress={requestInProgress}
            errorSubs={errorSubs}
            onNextChargeDate={(notificationData: NotificationProps) => {
              notify(notificationData);
            }}
          />
        )}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {ultraWashAd !== null && boxItems.length > 0 && (
            <Ad
              ad={ultraWashAd}
              onClick={() =>
                !containsUltraWash
                  ? handleAdClick(ultraWashAd)
                  : handleUltrawashUpgrade(ultraWashAd)
              }
              btnClass="bg-purple-800 not-disabled:hover:bg-purple-700"
              btnCopy={
                !containsUltraWash ? "Add to your next box" : "Upgrade NOW"
              }
            />
          )}
          {ad !== null && !isMobile && (
            <Ad
              ad={ad}
              onClick={() => handleAdClick(ad)}
              btnClass="bg-blue-900 not-disabled:hover:bg-blue-700"
              btnCopy="Add to your next box"
            />
          )}
        </div>
        {!firstShipment && (
          <div className="!mt-0 space-y-8">
            <h2 className="title flex-initial text-center text-2xl md:text-left">
              Your Next Box
            </h2>
            <p className="text-center italic md:text-left">
              No active subscriptions
            </p>
          </div>
        )}
        {firstShipment && (
          <div className="space-y-8">
            <div className="flex flex-col justify-between space-y-8 md:flex-row md:space-x-8 md:space-y-0 lg:items-center">
              <h2 className="title flex-initial text-center text-2xl lg:text-left">
                Your Next Box
              </h2>
              <div className="flex max-w-full flex-initial flex-col items-center justify-end lg:flex-row lg:space-x-4">
                {firstAvailableAddressId && (
                  <Button
                    className={`order-2 ${
                      activeDiscount ? "mt-4 lg:mt-0" : ""
                    } shrink-0 lg:order-1`}
                    variant="text"
                    onClick={() => {
                      setDiscountDrawerOpen(true);
                    }}
                    disabled={requestInProgress}
                  >
                    {activeDiscount
                      ? "Update Discount"
                      : "Have A Discount Code?"}
                  </Button>
                )}
                {firstAvailableAddressId && activeDiscount && (
                  <DiscountBadge
                    discountName={activeDiscount}
                    onChange={() => {
                      onRemoveDiscount(firstAvailableAddressId);
                    }}
                    disabled={requestInProgress}
                    className="order-1 max-w-full font-semibold shadow lg:order-2"
                  />
                )}
              </div>
            </div>
            <BoxList
              boxes={[firstShipment]}
              requestInProgress={requestInProgress}
              onSelect={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("edit");
              }}
              onSkip={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("skip");
              }}
              onDelay={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("delay");
              }}
              onAddItem={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setProductDrawerOpen(true);
              }}
              onOrderNow={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("order-now");
                setRootBoxDrawerTitle("Order Now");
              }}
              onDelayBox={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("delay-order");
                setRootBoxDrawerTitle("Delay This Box");
              }}
              onSkipBox={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("skip-shipment");
                setRootBoxDrawerTitle("Skip This Box");
              }}
            />
            {ad !== null && isMobile && (
              <Ad
                ad={ad}
                onClick={() => handleAdClick(ad)}
                btnClass="bg-blue-900 not-disabled:hover:bg-blue-700"
                btnCopy="Add to your next box"
              />
            )}
          </div>
        )}
        {remainingShipments.length > 0 && (
          <div className="space-y-8">
            <h2 className="title text-center text-2xl md:text-left">
              Future Shipments
            </h2>
            <BoxList
              boxes={remainingShipments}
              requestInProgress={requestInProgress}
              onSelect={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("edit");
              }}
              onDelay={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("delay");
              }}
              onSkip={(x) => {
                setSelectedOrderId(x.rechargeItem.id);
                setDrawerPage("skip");
              }}
              onAddItem={(x) => {
                setAddProductDrawerFilter(undefined);
                setSelectedOrderId(x.rechargeItem.id);
                setProductDrawerOpen(true);
              }}
              onOrderNow={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("order-now");
                setRootBoxDrawerTitle("Order Now");
              }}
              onDelayBox={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("delay-order");
                setRootBoxDrawerTitle("Delay This Box");
              }}
              onSkipBox={(x) => {
                setSelectedShipmentId(x);
                setShipmentDrawerPage("skip-shipment");
                setRootBoxDrawerTitle("Skip This Box");
              }}
            />
          </div>
        )}
        <InactiveSubscriptionsList
          onReactivate={handleReactivate}
          requestInProgress={requestInProgress}
        />
        <RootBoxItemDrawer
          open={drawerPage !== undefined}
          contents={(nav) =>
            drawerPage ? (
              <RootDrawerPage
                page={drawerPage}
                nextChargeDate={firstShipment?.nextChargeDate ?? new Date()}
                {...nav}
              />
            ) : null
          }
          selectedItem={selectedItem}
          onExit={() => setDrawerPage(undefined)}
          notify={notify}
        />
        <RootBoxDrawer
          open={ShipmentDrawerPage !== undefined}
          contents={(nav) =>
            ShipmentDrawerPage ? (
              <RootBoxDrawerPage
                box={selectedShipment}
                nextChargeDate={firstShipment?.nextChargeDate ?? new Date()}
                page={ShipmentDrawerPage}
                {...nav}
              />
            ) : null
          }
          selectedShipment={selectedShipment}
          onExit={() => setShipmentDrawerPage(undefined)}
          notify={notify}
          title={rootBoxDrawerTitle}
        />
        <AddProductDrawer
          notify={notify}
          open={isProductDrawerOpen}
          selectedBoxItem={selectedItem}
          onExit={() => setProductDrawerOpen(false)}
          initialFilter={addProductDrawerFilter}
          sourceModule={addProductDrawerFilter ? "portal_ad" : undefined}
        />
        <EditDiscountDrawer
          onExit={() => setDiscountDrawerOpen(false)}
          open={isDiscountDrawerOpen}
          notify={notify}
          addressId={firstAvailableAddressId}
          discountName={activeDiscount}
        />
      </Sentry.ErrorBoundary>
      <NotificationManagement notification={currNotification} />
    </div>
  );
};
